import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { vibrate } from '../utils/haptics';
import './StreakAnimation.css';

import { EmojiText } from './common/Picto';
interface StreakAnimationProps {
  show: boolean;
  streakCount?: number;
  type?: 'streak' | 'word-learned';
  onComplete?: () => void;
}

const getStreakEmoji = (count: number): string => {
  if (count >= 10) return '⚡🔥';
  if (count >= 5) return '🔥🔥';
  return '🔥';
};

const getStreakMessageKey = (count: number): string => {
  if (count >= 10) return 'streakAnimation.streak10';
  if (count >= 7) return 'streakAnimation.streak7';
  if (count >= 5) return 'streakAnimation.streak5';
  if (count >= 3) return 'streakAnimation.streak3';
  return 'streakAnimation.streak1';
};

const StreakAnimation: React.FC<StreakAnimationProps> = ({
  show,
  streakCount = 1,
  type = 'streak',
  onComplete
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (show && type === 'streak') vibrate('streak');
  }, [show, type]);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 1400);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  const isWordLearned = type === 'word-learned';

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={`streak-animation ${isWordLearned ? 'word-learned' : 'streak'}`}
          initial={{ opacity: 0, scale: 0.5, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -10 }}
          transition={{ type: 'spring', stiffness: 420, damping: 20 }}
        >
          <motion.div
            className="streak-emoji"
            animate={isWordLearned
              ? { scale: [1, 1.4, 1], rotate: [0, -15, 15, 0] }
              : { rotate: [0, -10, 10, -8, 8, 0] }
            }
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <EmojiText text={isWordLearned ? '⭐' : getStreakEmoji(streakCount)} />
          </motion.div>

          {!isWordLearned && streakCount >= 2 && (
            <motion.div
              className="streak-count"
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.3, 1] }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              x{streakCount}
            </motion.div>
          )}

          <div className="streak-message">
            {isWordLearned ? t('streakAnimation.wordLearned') : t(getStreakMessageKey(streakCount))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StreakAnimation;
